import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Send, Loader2, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SignUpPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedPlan = searchParams.get('plan') || '';

  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    organization: '',
    plan: preselectedPlan || 'starter',
    num_tvs: '1',
    message: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error('Please fill in your name and email');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name,
        email: form.email,
        phone: form.phone,
        subject: `Sign Up - ${form.plan.charAt(0).toUpperCase() + form.plan.slice(1)} Plan (${form.num_tvs} TV${form.num_tvs !== '1' ? 's' : ''})`,
        message: `Organization: ${form.organization || 'N/A'}\nPlan: ${form.plan}\nNumber of TVs: ${form.num_tvs}\n\nAdditional Info: ${form.message || 'None'}`,
      });
      setSubmitted(true);
      toast.success('Sign up request submitted!');
    } catch (error) {
      toast.error('Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center px-6">
        <div className="max-w-md w-full text-center">
          <div className="w-20 h-20 bg-cyan-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-cyan-400" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-4">You're In!</h1>
          <p className="text-slate-400 mb-8">
            Thanks for signing up, {form.name}! We'll be in touch within 24 hours to get your displays set up.
          </p>
          <div className="flex flex-col gap-3">
            <Button
              onClick={() => navigate('/pricing')}
              className="bg-cyan-500 hover:bg-cyan-600 w-full"
            >
              Back to Pricing
            </Button>
            <Button
              onClick={() => navigate('/')}
              variant="outline"
              className="border-slate-700 text-slate-300 hover:bg-slate-800 w-full"
            >
              Back to Home
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950">
      {/* Header */}
      <header className="bg-slate-900/95 backdrop-blur-lg border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <div className="flex items-center space-x-3 cursor-pointer" onClick={() => navigate('/')}>
              <img
                src="https://customer-assets.emergentagent.com/job_frosty-it-hub/artifacts/o2t5vwwy_logo.jpeg"
                alt="FrostByte IT Logo"
                className="h-12 w-12 object-contain"
              />
              <span className="text-2xl font-bold text-white">
                FrostByte<span className="text-cyan-400">IT</span>
              </span>
            </div>
            <Button
              onClick={() => navigate('/pricing')}
              variant="outline"
              className="border-slate-700 text-slate-300 hover:bg-slate-800"
            >
              <ArrowLeft className="w-4 h-4 mr-2" /> Back to Pricing
            </Button>
          </div>
        </div>
      </header>

      {/* Form */}
      <section className="py-20 px-6">
        <div className="max-w-xl mx-auto">
          <div className="text-center mb-10">
            <h1 className="text-3xl sm:text-4xl font-bold text-white mb-3">
              Get Started with <span className="text-cyan-400">FrostByte</span>
            </h1>
            <p className="text-slate-400">
              Fill out the form below and we'll have your digital signage up and running in no time.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-slate-400 mb-1 block">Full Name *</label>
                <Input
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  placeholder="John Smith"
                  className="bg-slate-900 border-slate-700 text-white"
                  required
                  data-testid="signup-name"
                />
              </div>
              <div>
                <label className="text-sm text-slate-400 mb-1 block">Email *</label>
                <Input
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={handleChange}
                  placeholder="john@example.com"
                  className="bg-slate-900 border-slate-700 text-white"
                  required
                  data-testid="signup-email"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-slate-400 mb-1 block">Phone</label>
                <Input
                  name="phone"
                  value={form.phone}
                  onChange={handleChange}
                  placeholder="(555) 123-4567"
                  className="bg-slate-900 border-slate-700 text-white"
                  data-testid="signup-phone"
                />
              </div>
              <div>
                <label className="text-sm text-slate-400 mb-1 block">Organization</label>
                <Input
                  name="organization"
                  value={form.organization}
                  onChange={handleChange}
                  placeholder="Your business or org name"
                  className="bg-slate-900 border-slate-700 text-white"
                  data-testid="signup-org"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-slate-400 mb-1 block">Plan</label>
                <select
                  name="plan"
                  value={form.plan}
                  onChange={handleChange}
                  className="w-full bg-slate-900 border border-slate-700 text-white rounded-md px-3 py-2 text-sm"
                  data-testid="signup-plan"
                >
                  <option value="nonprofit">Nonprofit - $8/mo per TV</option>
                  <option value="starter">Starter - $15/mo per TV</option>
                  <option value="business">Business - $25/mo per TV</option>
                  <option value="enterprise">Enterprise - Custom</option>
                </select>
              </div>
              <div>
                <label className="text-sm text-slate-400 mb-1 block">Number of TVs</label>
                <Input
                  name="num_tvs"
                  type="number"
                  min="1"
                  value={form.num_tvs}
                  onChange={handleChange}
                  className="bg-slate-900 border-slate-700 text-white"
                  data-testid="signup-tvs"
                />
              </div>
            </div>

            {/* Price estimate */}
            {form.plan !== 'enterprise' && (
              <div className="p-4 bg-cyan-500/10 border border-cyan-500/20 rounded-lg">
                <div className="flex justify-between items-center">
                  <span className="text-slate-300 text-sm">Estimated monthly cost:</span>
                  <span className="text-cyan-400 text-xl font-bold">
                    ${(form.plan === 'nonprofit' ? 8 : form.plan === 'starter' ? 15 : 25) * (parseInt(form.num_tvs) || 1)}/mo
                  </span>
                </div>
              </div>
            )}

            <div>
              <label className="text-sm text-slate-400 mb-1 block">Anything else we should know?</label>
              <textarea
                name="message"
                value={form.message}
                onChange={handleChange}
                placeholder="Tell us about your setup, locations, or any questions..."
                rows={3}
                className="w-full bg-slate-900 border border-slate-700 text-white rounded-md px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-cyan-500"
                data-testid="signup-message"
              />
            </div>

            <Button
              type="submit"
              disabled={submitting}
              className="w-full bg-cyan-500 hover:bg-cyan-600 text-white py-3 text-lg shadow-lg shadow-cyan-500/20"
              data-testid="signup-submit-btn"
            >
              {submitting ? (
                <Loader2 className="w-5 h-5 animate-spin mr-2" />
              ) : (
                <Send className="w-5 h-5 mr-2" />
              )}
              {submitting ? 'Submitting...' : 'Submit Sign Up'}
            </Button>

            <p className="text-slate-500 text-xs text-center">
              By signing up, you agree to be contacted by FrostByte IT regarding your digital signage needs.
            </p>
          </form>
        </div>
      </section>
    </div>
  );
};

export default SignUpPage;
